<template>
    <div class="loginPage">
        <div class="loginCard">
            <h1>Login</h1>
            <p class="loginIntro">
                Enter your email address and we will send a magic link you can use to log in.
            </p>

            <form class="loginForm" @submit.prevent="submitMagicLink">
                <label for="loginEmail" class="label">Email address</label>
                <input
                    id="loginEmail"
                    ref="emailInput"
                    v-model.trim="email"
                    class="form-control"
                    type="email"
                    autocomplete="email"
                    placeholder="name@example.com"
                    required
                />

                <button class="btn btn-primary" type="submit" :disabled="submitting || !isValidEmail">
                    {{ submitting ? "Sending..." : "Send Magic Link" }}
                </button>
            </form>

            <p v-if="status === 'success'" class="statusText statusSuccess">
                If a matching active user exists, a magic link has been sent to {{ submittedEmail }}.
            </p>
            <p v-else-if="status === 'error'" class="statusText statusError">
                {{ error }}
            </p>
        </div>
    </div>
</template>

<script>
import { apiFetchJson } from "@/libs/api";
import { isAuthenticated } from "@/libs/auth";

export default {
    name: "LoginView",
    display: "Login",
    data() {
        return {
            email: "",
            submitting: false,
            status: "idle",
            error: "",
            submittedEmail: "",
        };
    },
    computed: {
        isValidEmail() {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
        },
    },
    methods: {
        getFrontendUri() {
            if (typeof window !== "undefined" && window.location?.origin) {
                return window.location.origin;
            }
            return (process.env.VUE_APP_ROOT_URL || "https://qrcodeexperts.net").replace(/\/+$/, "");
        },
        async submitMagicLink() {
            if (!this.isValidEmail || this.submitting) return;

            this.submitting = true;
            this.status = "idle";
            this.error = "";

            try {
                await apiFetchJson("magiclink", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        email: this.email,
                        frontendUri: this.getFrontendUri(),
                    }),
                }, { auth: false });
                this.submittedEmail = this.email;
                this.status = "success";
                this.email = "";
            } catch (err) {
                this.status = "error";
                this.error = err?.message || "Unable to send magic link.";
            } finally {
                this.submitting = false;
            }
        },
    },
    mounted() {
        if (isAuthenticated()) {
            this.$router.replace({ path: "/" });
            return;
        }
        this.$nextTick(() => {
            const el = this.$refs.emailInput;
            if (el && typeof el.focus === "function") el.focus();
        });
    },
}
</script>

<style scoped>
.loginPage {
    padding: 20px 12px;
}

.loginCard {
    max-width: 586px;
    margin: 20px auto;
    background: var(--qrce-surface);
    text-align: left;
    padding: 20px;
    border-radius: var(--qrce-radius);
    border: 1px solid var(--qrce-border);
    border-left: 4px solid var(--qrce-primary);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
}

.loginIntro {
    color: var(--qrce-muted);
    margin-bottom: 16px;
}

.label {
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}

.loginForm {
    display: grid;
    gap: 12px;
}

.loginCard :deep(input.form-control) {
    background: var(--qrce-surface-2);
    border: 1px solid rgba(15, 23, 42, 0.22);
    border-radius: 12px;
    color: var(--qrce-text);
    padding: 10px 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.statusText {
    margin-top: 16px;
    margin-bottom: 0;
}

.statusSuccess {
    color: #166534;
}

.statusError {
    color: #b91c1c;
}

@media (max-width: 819px) {
    .loginCard {
        width: calc(100% - 24px);
        margin: 14px 12px;
    }
}
</style>
